// Generic entity types for the unified entity detail system

import { Organization, Project, Source } from "../services/graphService";
import type { EntityTypeKey } from "./entityRegistry";

// Generic entity detail response that can contain any entity type
export interface EntityDetailResponse {
  // Facility entity (existing structure)
  f?: {
    id: string;
    name: string;
    facility_type: string[];
    labels: string[];
    description: string;
    phases: any[];
    operational_from: string;
    number_of_employees: number;
    services: string[];
    production_kpis: any[];
    technologies_used: string[];
    products_produced: string[];
    product_use_cases: string[];
    naics_codes_with_title: string[];
    input_hs_codes_with_title: string[];
    output_hs_codes_with_title: string[];
    confidence_level: number;
    n_sources: number;
    country_iso2: string;
    address: string;
    latitude: number;
    longitude: number;
    precision_level: string;
  };

  // Organization entity
  o?: Organization;

  // Project entity
  p?: Project;

  // Source entity
  s?: Source;

  // Common relationship arrays (present for all entity types)
  organizations?: {
    organization: any;
    relationship: {
      relation_type?: string;
      semantic_role?: string;
      [key: string]: any;
    };
  }[];

  projects?: {
    project: any;
    relationship: {
      relation_type?: string;
      semantic_role?: string;
      project_role?: string;
      [key: string]: any;
    };
  }[];

  sources?: {
    source: any;
    relationship: {
      relation_type?: string;
      semantic_role?: string;
      [key: string]: any;
    };
  }[];

  // For organizations, they might have facilities
  facilities?: {
    facility: any;
    relationship: {
      relation_type?: string;
      semantic_role?: string;
      [key: string]: any;
    };
  }[];
}

// Helper type to extract the main entity from the response
export type MainEntity = {
  id: string;
  name: string;
  description?: string;
  entityType: EntityTypeKey;
  [key: string]: any;
};

// Helper function to extract the main entity from the response
export const extractMainEntity = (
  response: EntityDetailResponse
): MainEntity | null => {
  if (response.f) {
    return {
      ...response.f,
      entityType: "facility" as const,
    };
  }

  if (response.o) {
    return {
      ...response.o,
      entityType: "organization" as const,
    };
  }

  if (response.p) {
    return {
      ...response.p,
      entityType: "project" as const,
    };
  }

  if (response.s) {
    return {
      ...response.s,
      name: response.s.title, // Sources use 'title' instead of 'name'
      entityType: "source" as const,
    };
  }

  return null;
};

// Helper function to get entity type from ID prefix
export const getEntityTypeFromId = (
  entityId: string
): EntityTypeKey | "unknown" => {
  if (entityId.startsWith("fac_")) return "facility";
  if (entityId.startsWith("org_")) return "organization";
  if (entityId.startsWith("pro_")) return "project";
  if (entityId.startsWith("doc_")) return "source";
  return "unknown";
};
